import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useUploadEpisode, useMovies, useMyMovies } from '../hooks/useMovie';
import { useAuthStore } from '../store';
import { Button } from '../components/ui/button';
import { SearchableSelect } from '../components/ui/searchable-select';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
        {hint && <span className="ml-1 font-normal text-muted-foreground/60">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

export default function UploadEpisodePage() {
  usePageTitle('Upload Episode · Kast');
  const { mutateAsync, isPending } = useUploadEpisode();
  const { user } = useAuthStore();
  const isAdmin = user?.Role === 'Admin';
  // Creators can only add episodes to titles they uploaded themselves (the server enforces this
  // too), so they pick from their own titles — minus rejected ones. Admins pick from the whole
  // live catalogue. Only the list that applies is fetched.
  const { data: allMovies = [], error: allMoviesError } = useMovies(isAdmin);
  const { data: myMovies = [], error: myMoviesError } = useMyMovies(!isAdmin);
  const movies = isAdmin ? allMovies : myMovies.filter((m) => m.ApprovalStatus !== 'Rejected');
  const moviesError = isAdmin ? allMoviesError : myMoviesError;

  const [form, setForm] = useState({
    movieId: '', seasonNumber: '', episodeNumber: '', title: '', description: '',
  });
  const [videoFile, setVideoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const resetForm = () => {
    setForm({ movieId: '', seasonNumber: '', episodeNumber: '', title: '', description: '' });
    setVideoFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.movieId) { toast.error('Select a movie'); return; }
    if (!form.episodeNumber) { toast.error('Episode number is required'); return; }
    if (!form.title.trim()) { toast.error('Episode title is required'); return; }

    const fd = new FormData();
    fd.append('MovieId', form.movieId);
    if (form.seasonNumber) fd.append('SeasonNumber', form.seasonNumber);
    fd.append('EpisodeNumber', form.episodeNumber);
    fd.append('Title', form.title.trim());
    if (form.description) fd.append('Description', form.description);
    if (videoFile) fd.append('VideoFile', videoFile);

    setUploadProgress(0);
    try {
      const { data } = await mutateAsync({
        formData: fd,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      if (data.success) {
        toast.success('Episode uploaded! Pending admin approval.');
        resetForm();
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Upload failed'));
    } finally {
      setUploadProgress(0);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
          <Icon icon="ph:upload-simple-bold" className="size-5" />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Upload Episode</h1>
          <p className="text-xs text-muted-foreground">Your episode will be reviewed by an admin before it goes live</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        encType="multipart/form-data"
        className="flex flex-col gap-4 rounded-xl bg-white/[0.03] p-5 ring-1 ring-foreground/[0.06] sm:p-6"
      >
        <Field label="Movie" required>
          {moviesError && (
            <p className="mb-1.5 text-xs text-destructive">{getApiErrorMessage(moviesError, 'Failed to load movies.')}</p>
          )}
          <SearchableSelect
            value={form.movieId}
            onValueChange={(value) => setForm((f) => ({ ...f, movieId: value }))}
            options={movies.map((m) => ({ value: String(m.Id), label: m.Title }))}
            placeholder="— Select movie —"
            searchPlaceholder="Search movies…"
            emptyText={isAdmin ? 'No movies found.' : "You haven't uploaded any titles yet."}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Season Number" hint="(optional)">
            <input
              name="seasonNumber"
              type="number"
              className={inputClass}
              value={form.seasonNumber}
              onChange={handleChange}
              placeholder="e.g. 1"
              min="1"
            />
          </Field>
          <Field label="Episode Number" required>
            <input
              name="episodeNumber"
              type="number"
              className={inputClass}
              value={form.episodeNumber}
              onChange={handleChange}
              placeholder="e.g. 1"
              min="1"
              required
            />
          </Field>
        </div>

        <Field label="Episode Title" required>
          <input
            name="title"
            className={inputClass}
            value={form.title}
            onChange={handleChange}
            placeholder="Episode title"
            required
          />
        </Field>

        <Field label="Description">
          <textarea
            name="description"
            className={`${inputClass} resize-none`}
            value={form.description}
            onChange={handleChange}
            rows={3}
            placeholder="Brief episode description…"
          />
        </Field>

        <Field label="Video File">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
            <Icon icon="ph:video-camera-bold" className="size-4 shrink-0" />
            <span className="truncate">{videoFile ? videoFile.name : 'Choose video file…'}</span>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </label>
        </Field>

        <Button type="submit" disabled={isPending} className="mt-1 gap-1.5 self-start">
          {isPending ? (
            <>
              <Icon icon="ph:spinner-gap-bold" className="animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Icon icon="ph:upload-simple-bold" />
              Upload Episode
            </>
          )}
        </Button>

        {isPending && (
          <div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Finishing up…'}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
